import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import { useAuditLogList } from '../hooks/useAuditLogList';
import { useAuditActions } from '../hooks/useAuditActions';
import { usePurgeAuditLogs } from '../hooks/usePurgeAuditLogs';
import { useBulkDeleteAuditLogs } from '../hooks/useBulkDeleteAuditLogs';
import { AuditLogFilterBar } from '../components/AuditLogFilterBar';
import { AuditLogDrawer } from '../components/AuditLogDrawer';
import { ExportButton } from '../components/ExportButton';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import { useHasPermission } from '../../_shared/hooks/useCurrentUser';
import type { DataTableColumn, StatusTone } from '@ui-kit';
import type { AuditLogListItem, AuditLogSearchCondition, ApiError, AuditResult, AuditSeverity } from '../types/auditLog';

const DEFAULT_PAGE_SIZE = 20;

const DEFAULT_FILTERS: AuditLogSearchCondition = {
  page: 0,
  size: DEFAULT_PAGE_SIZE,
  sort: 'occurredAt,desc',
};

function resultTone(result: AuditResult): StatusTone {
  if (result === 'SUCCESS') return 'success';
  if (result === 'DENY') return 'warning';
  return 'danger';
}

// resultLabel is resolved via t() in the component — this helper is replaced inline

function severityRowBg(severity: AuditSeverity): string | undefined {
  if (severity === 'ALERT' || severity === 'CRITICAL') {
    return 'color-mix(in srgb, var(--color-danger) 8%, transparent)';
  }
  return undefined;
}

/**
 * locale-aware 날짜/시간 포맷 (R7-096 fix).
 * locale 파라미터는 i18n.language 를 주입받아 ko/en 전환에 대응한다.
 */
function formatDateTime(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

/** Skeleton 행 */
function SkeletonRow() {
  return (
    <tr>
      {[60, 55, 50, 65, 30, 40].map((w, i) => (
        <td
          key={i}
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div
            style={{
              height: 12,
              width: `${w}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

/**
 * AuditLogListPage — 감사 로그 목록 (ROLE_ADMIN)
 * /audit-logs 라우트
 */
export function AuditLogListPage() {
  const { t, i18n } = useTranslation();
  const [pendingFilters, setPendingFilters] = useState<AuditLogSearchCondition>({});
  const [appliedFilters, setAppliedFilters] = useState<AuditLogSearchCondition>(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [purgeStep, setPurgeStep] = useState<0 | 1 | 2>(0);
  const [purgeConfirmInput, setPurgeConfirmInput] = useState('');
  const [purgeToast, setPurgeToast] = useState<string | null>(null);

  // #453 UAT 2026-05-14 r2: 행 선택 + 일괄 삭제 상태
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkToast, setBulkToast] = useState<string | null>(null);
  const canBulkDelete = useHasPermission('AUDIT_DELETE');
  const bulkDeleteMutation = useBulkDeleteAuditLogs();

  const purgeMutation = usePurgeAuditLogs();

  const handlePurgeOpen = useCallback(() => {
    setPurgeStep(1);
    setPurgeConfirmInput('');
  }, []);

  const handlePurgeCancel = useCallback(() => {
    setPurgeStep(0);
    setPurgeConfirmInput('');
  }, []);

  const handlePurgeStep1Confirm = useCallback(() => {
    setPurgeStep(2);
  }, []);

  const handlePurgeSubmit = useCallback(() => {
    if (purgeConfirmInput !== 'DELETE') return;
    purgeMutation.mutate(undefined, {
      onSuccess: (data) => {
        setPurgeStep(0);
        setPurgeConfirmInput('');
        setPurgeToast(t('auditLog.purge.success', { count: data.deletedCount }));
        setTimeout(() => setPurgeToast(null), 4000);
      },
      onError: () => {
        setPurgeStep(0);
        setPurgeConfirmInput('');
        setPurgeToast(t('auditLog.purge.error'));
        setTimeout(() => setPurgeToast(null), 4000);
      },
    });
  }, [purgeConfirmInput, purgeMutation, t]);

  const listQuery = useAuditLogList(appliedFilters);
  const actionsQuery = useAuditActions();

  const pageData = listQuery.data;

  const handleFiltersChange = useCallback(
    (partial: Partial<AuditLogSearchCondition>) => {
      setPendingFilters((prev) => ({ ...prev, ...partial }));
    },
    []
  );

  const handleSearch = useCallback(() => {
    const next: AuditLogSearchCondition = {
      ...pendingFilters,
      page: 0,
      size: DEFAULT_PAGE_SIZE,
      sort: 'occurredAt,desc',
    };
    // strip empty string values
    Object.keys(next).forEach((k) => {
      const key = k as keyof AuditLogSearchCondition;
      if (next[key] === '' || next[key] === undefined) {
        delete next[key];
      }
    });
    setPage(0);
    setAppliedFilters({ ...next, page: 0, size: DEFAULT_PAGE_SIZE, sort: 'occurredAt,desc' });
    setSelectedIds(new Set());
  }, [pendingFilters]);

  const handleReset = useCallback(() => {
    setPendingFilters({});
    setPage(0);
    setAppliedFilters(DEFAULT_FILTERS);
    setSelectedIds(new Set());
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    setAppliedFilters((prev) => ({ ...prev, page: newPage }));
    setSelectedIds(new Set());
  }, []);

  // #453: 행 선택 토글
  const toggleSelectOne = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // #453: 현재 페이지 전체 토글 (헤더 체크박스)
  const togglePageAll = useCallback((pageRows: AuditLogListItem[]) => {
    setSelectedIds((prev) => {
      const pageIds = pageRows.map((r) => r.id);
      const allOnPageSelected = pageIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allOnPageSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, []);

  const handleBulkDeleteSubmit = useCallback(() => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    bulkDeleteMutation.mutate(ids, {
      onSuccess: (data) => {
        setBulkConfirmOpen(false);
        setSelectedIds(new Set());
        setBulkToast(t('auditLog.bulkDelete.success', { count: data.deletedCount }));
        setTimeout(() => setBulkToast(null), 4000);
      },
      onError: () => {
        setBulkConfirmOpen(false);
        setBulkToast(t('auditLog.bulkDelete.error'));
        setTimeout(() => setBulkToast(null), 4000);
      },
    });
  }, [selectedIds, bulkDeleteMutation, t]);

  const handleRowClick = useCallback((row: AuditLogListItem) => {
    setSelectedId(row.id);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setSelectedId(null);
  }, []);

  const TABLE_HEADERS = [
    t('auditLog.list.column.occurredAt'),
    t('auditLog.list.column.action'),
    t('auditLog.list.column.resource'),
    t('auditLog.list.column.actor'),
    t('auditLog.list.column.result'),
    t('auditLog.list.column.ip'),
  ];

  // #453 UAT 2026-05-14 r2: admin 권한일 때만 행 선택 체크박스 컬럼 노출.
  // 컬럼 정의 시점에 현재 페이지 행 목록 + 선택 상태가 모두 반영되도록 의존성에 포함.
  const currentPageRows = pageData?.content ?? [];
  const allOnPageSelected =
    currentPageRows.length > 0 && currentPageRows.every((r) => selectedIds.has(r.id));
  const someOnPageSelected =
    !allOnPageSelected && currentPageRows.some((r) => selectedIds.has(r.id));

  const selectColumn: DataTableColumn<AuditLogListItem> | null = useMemo(() => {
    if (!canBulkDelete) return null;
    return {
      key: '__select',
      header: '',
      render: (row) => (
        <input
          type="checkbox"
          aria-label={t('auditLog.bulkDelete.row.aria', { id: row.id })}
          checked={selectedIds.has(row.id)}
          onChange={() => toggleSelectOne(row.id)}
          onClick={(e) => e.stopPropagation()}
          style={{ cursor: 'pointer' }}
        />
      ),
      width: '36px',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canBulkDelete, selectedIds, t]);

  // locale 변경에도 열 정의가 재생성되도록 i18n.language 의존성 포함 (R7-096, R7-080 fix)
  const baseColumns: DataTableColumn<AuditLogListItem>[] = useMemo(() => [
    {
      key: 'occurredAt',
      header: t('auditLog.list.column.occurredAt'),
      render: (row) => (
        <span
          style={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {formatDateTime(row.occurredAt, i18n.language)}
        </span>
      ),
    },
    {
      key: 'action',
      header: t('auditLog.list.column.action'),
      // R7-080: i18n 키 기반 한글/영문 라벨만 표시 — 영문 enum 혼합 제거
      render: (row) => {
        const i18nKey = `auditLog.action.${row.action}`;
        const label = t(i18nKey, { defaultValue: row.action });
        return (
          <span
            style={{
              fontFamily: 'var(--font-family-mono)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              color: 'var(--color-text-primary)',
            }}
          >
            {label}
          </span>
        );
      },
    },
    {
      key: 'resource',
      header: t('auditLog.list.column.resource'),
      render: (row) => (
        <span
          style={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {row.resourceType
            ? `${row.resourceType}${row.resourceId ? `/${row.resourceId}` : ''}`
            : '-'}
        </span>
      ),
    },
    {
      key: 'actor',
      header: t('auditLog.list.column.actor'),
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
          {row.actor ?? '-'}
        </span>
      ),
    },
    {
      key: 'result',
      header: t('auditLog.list.column.result'),
      render: (row) => (
        <StatusPill tone={resultTone(row.result)}>{t(`auditLog.result.${row.result}`)}</StatusPill>
      ),
    },
    {
      key: 'ip',
      header: t('auditLog.list.column.ip'),
      render: (row) => (
        <span
          style={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
          }}
        >
          {row.ip ?? '-'}
        </span>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, i18n.language]);

  // #453: select 컬럼 prepend (admin 전용)
  const columns: DataTableColumn<AuditLogListItem>[] = useMemo(
    () => (selectColumn ? [selectColumn, ...baseColumns] : baseColumns),
    [selectColumn, baseColumns]
  );

  const isListLoading = listQuery.isLoading;
  const isListError = listQuery.isError;
  const isEmpty = !isListLoading && !isListError && (pageData?.empty ?? false);
  const showTable = !isListLoading && !isListError && !isEmpty && !!pageData;

  return (
    <div style={{ padding: 0 }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('auditLog.list.title')}
        breadcrumb={<span>{t('auditLog.list.breadcrumb')}</span>}
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
            <ExportButton filters={appliedFilters} />
            {/* #453: 선택 N건 삭제 — admin 권한 + 1+ 선택 시 노출 */}
            {canBulkDelete && selectedIds.size > 0 && (
              <button
                type="button"
                onClick={() => setBulkConfirmOpen(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  background: 'var(--color-danger)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                {t('auditLog.bulkDelete.button', { count: selectedIds.size })}
              </button>
            )}
            <ButtonGuard requirePermission="AUDIT_DELETE_ALL">
              {({ disabled, reason }) => (
                <button
                  type="button"
                  disabled={disabled}
                  title={reason}
                  onClick={handlePurgeOpen}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    background: disabled ? 'var(--color-surface-muted)' : 'var(--color-danger)',
                    color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                    opacity: disabled ? 0.6 : 1,
                  }}
                >
                  {t('auditLog.purge.button')}
                </button>
              )}
            </ButtonGuard>
          </div>
        }
      />

      {/* 요약 정보 */}
      {pageData && (
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            margin: 'var(--spacing-2) 0 var(--spacing-4)',
          }}
        >
          {pageData.totalElements > 0
            ? t('auditLog.list.totalCount', { count: pageData.totalElements })
            : t('auditLog.list.noResults')}
        </p>
      )}

      {/* 필터 바 */}
      <AuditLogFilterBar
        filters={pendingFilters}
        actions={actionsQuery.data ?? []}
        onFiltersChange={handleFiltersChange}
        onSearch={handleSearch}
        onReset={handleReset}
        isLoading={isListLoading}
      />

      {/* 컨텐츠 영역 — 5상태 */}
      <div
        style={{
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--color-surface-background)',
        }}
      >
        {/* Loading — skeleton */}
        {isListLoading && (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            aria-label={t('auditLog.list.loadingAriaLabel')}
            aria-busy="true"
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {TABLE_HEADERS.map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-3) var(--spacing-4)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </tbody>
          </table>
        )}

        {/* Error */}
        {isListError && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <ErrorState
              title={t('auditLog.list.error.loadFailed')}
              message={
                (listQuery.error as unknown as ApiError)?.code === 'AUTH-403-001'
                  ? t('auditLog.list.error.forbidden')
                  : ((listQuery.error as unknown as ApiError)?.message ??
                    t('auditLog.error.network'))
              }
              onRetry={() => void listQuery.refetch()}
            />
          </div>
        )}

        {/* Empty */}
        {isEmpty && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <EmptyState
              title={t('auditLog.list.empty.title')}
              description={t('auditLog.list.empty.description')}
              action={
                <button
                  type="button"
                  onClick={handleReset}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    background: 'var(--color-brand-primary)',
                    color: 'var(--color-text-inverse)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                  }}
                >
                  {t('auditLog.list.button.resetFilters')}
                </button>
              }
            />
          </div>
        )}

        {/* Default — DataTable */}
        {showTable && pageData && (
          <>
            <div role="region" aria-label={t('auditLog.list.tableAriaLabel')} style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 'var(--font-size-sm)',
                }}
                aria-label={t('auditLog.list.tableAriaLabel')}
              >
                <thead>
                  <tr style={{ background: 'var(--color-surface-muted)' }}>
                    {columns.map((col) => (
                      <th
                        key={String(col.key)}
                        style={{
                          textAlign: 'left',
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          width: col.width,
                        }}
                      >
                        {col.key === '__select' ? (
                          <input
                            type="checkbox"
                            aria-label={t('auditLog.bulkDelete.header.aria')}
                            checked={allOnPageSelected}
                            ref={(el) => { if (el) el.indeterminate = someOnPageSelected; }}
                            onChange={() => togglePageAll(currentPageRows)}
                            onClick={(e) => e.stopPropagation()}
                            style={{ cursor: 'pointer' }}
                          />
                        ) : (
                          col.header
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageData.content.map((row) => (
                    <tr
                      key={row.id}
                      tabIndex={0}
                      role="button"
                      aria-label={t('auditLog.list.rowAriaLabel', { id: row.id })}
                      onClick={() => handleRowClick(row)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleRowClick(row);
                        }
                      }}
                      style={{
                        cursor: 'pointer',
                        background: severityRowBg(row.severity),
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background =
                          'var(--color-surface-muted)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background =
                          severityRowBg(row.severity) ?? '';
                      }}
                    >
                      {columns.map((col) => (
                        <td
                          key={String(col.key)}
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            borderBottom: '1px solid var(--color-surface-border)',
                          }}
                        >
                          {col.render ? col.render(row) : String(row[col.key as keyof AuditLogListItem] ?? '-')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'var(--color-surface-muted)',
                borderTop: '1px solid var(--color-surface-border)',
                flexWrap: 'wrap',
                gap: 'var(--spacing-2)',
              }}
            >
              <span
                style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}
              >
                {t('auditLog.list.pagination.showing', {
                  total: pageData.totalElements.toLocaleString(),
                  from: pageData.number * pageData.size + 1,
                  to: Math.min((pageData.number + 1) * pageData.size, pageData.totalElements),
                })}
              </span>
              <nav aria-label={t('auditLog.list.pagination.nav')} style={{ display: 'inline-flex', gap: 'var(--spacing-1)' }}>
                <button
                  type="button"
                  disabled={pageData.first}
                  onClick={() => handlePageChange(page - 1)}
                  aria-label={t('auditLog.list.pagination.prev')}
                  style={{
                    minWidth: 28,
                    height: 28,
                    padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.first ? 'not-allowed' : 'pointer',
                    opacity: pageData.first ? 0.5 : 1,
                  }}
                >
                  &lt;
                </button>
                {Array.from({ length: Math.min(pageData.totalPages, 10) }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handlePageChange(i)}
                    aria-label={t('auditLog.list.pagination.page', { page: i + 1 })}
                    aria-current={i === pageData.number ? 'page' : undefined}
                    style={{
                      minWidth: 28,
                      height: 28,
                      padding: '0 var(--spacing-2)',
                      borderRadius: 'var(--radius-sm)',
                      background:
                        i === pageData.number
                          ? 'var(--color-brand-primary)'
                          : 'var(--color-surface-elevated)',
                      border: `1px solid ${
                        i === pageData.number
                          ? 'var(--color-brand-primary)'
                          : 'var(--color-surface-border)'
                      }`,
                      color:
                        i === pageData.number
                          ? 'var(--color-text-inverse)'
                          : 'var(--color-text-secondary)',
                      fontSize: 'var(--font-size-xs)',
                      cursor: 'pointer',
                      fontWeight: i === pageData.number ? 600 : 400,
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={pageData.last}
                  onClick={() => handlePageChange(page + 1)}
                  aria-label={t('auditLog.list.pagination.next')}
                  style={{
                    minWidth: 28,
                    height: 28,
                    padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.last ? 'not-allowed' : 'pointer',
                    opacity: pageData.last ? 0.5 : 1,
                  }}
                >
                  &gt;
                </button>
              </nav>
            </div>
          </>
        )}
      </div>

      {/* Drawer — dialog 상태. #454: 상세 [삭제] 성공 시 토스트 + 닫힘 + invalidate */}
      {selectedId !== null && (
        <AuditLogDrawer
          logId={selectedId}
          onClose={handleDrawerClose}
          onDeleted={() => {
            setBulkToast(t('auditLog.detail.delete.success'));
            setTimeout(() => setBulkToast(null), 4000);
            handleDrawerClose();
          }}
        />
      )}

      {/* #453: 일괄 삭제 confirm 다이얼로그 */}
      {bulkConfirmOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="bulk-delete-title"
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.45)',
          }}
        >
          <div
            style={{
              background: 'var(--color-surface-background)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-6)',
              width: 420,
              maxWidth: '90vw',
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            <h2
              id="bulk-delete-title"
              style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--spacing-3)' }}
            >
              {t('auditLog.bulkDelete.confirm.title')}
            </h2>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-5)' }}>
              {t('auditLog.bulkDelete.confirm.body', { count: selectedIds.size })}
            </p>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setBulkConfirmOpen(false)}
                disabled={bulkDeleteMutation.isPending}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  cursor: 'pointer',
                }}
              >
                {t('auditLog.bulkDelete.confirm.cancel')}
              </button>
              <button
                type="button"
                onClick={handleBulkDeleteSubmit}
                disabled={bulkDeleteMutation.isPending || selectedIds.size === 0}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-danger)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: bulkDeleteMutation.isPending ? 'not-allowed' : 'pointer',
                  opacity: bulkDeleteMutation.isPending ? 0.6 : 1,
                }}
              >
                {bulkDeleteMutation.isPending ? '...' : t('auditLog.bulkDelete.confirm.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* #453: 일괄 삭제 / #454: 상세 삭제 결과 토스트 */}
      {bulkToast !== null && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 1100,
            background: 'var(--color-surface-overlay, #1e1e2e)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {bulkToast}
        </div>
      )}

      {/* 퍼지 다이얼로그 — Step 1 */}
      {purgeStep === 1 && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="purge-dialog-title"
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.45)',
          }}
        >
          <div
            style={{
              background: 'var(--color-surface-background)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-6)',
              width: 420,
              maxWidth: '90vw',
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            <h2
              id="purge-dialog-title"
              style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--spacing-3)' }}
            >
              {t('auditLog.purge.step1.title')}
            </h2>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-5)' }}>
              {t('auditLog.purge.step1.body')}
            </p>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handlePurgeCancel}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  cursor: 'pointer',
                }}
              >
                {t('auditLog.purge.step1.cancel')}
              </button>
              <button
                type="button"
                onClick={handlePurgeStep1Confirm}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-danger)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {t('auditLog.purge.step1.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 퍼지 다이얼로그 — Step 2 (타이핑 확인) */}
      {purgeStep === 2 && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="purge-confirm-title"
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.45)',
          }}
        >
          <div
            style={{
              background: 'var(--color-surface-background)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-6)',
              width: 420,
              maxWidth: '90vw',
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            <h2
              id="purge-confirm-title"
              style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--spacing-3)' }}
            >
              {t('auditLog.purge.step2.title')}
            </h2>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-3)' }}>
              {t('auditLog.purge.step2.body')}
            </p>
            <input
              type="text"
              value={purgeConfirmInput}
              onChange={(e) => setPurgeConfirmInput(e.target.value)}
              placeholder={t('auditLog.purge.step2.input.placeholder')}
              autoFocus
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                marginBottom: 'var(--spacing-4)',
                fontFamily: 'var(--font-family-mono)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
              }}
            />
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handlePurgeCancel}
                disabled={purgeMutation.isPending}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  cursor: 'pointer',
                }}
              >
                {t('auditLog.purge.step2.cancel')}
              </button>
              <button
                type="button"
                onClick={handlePurgeSubmit}
                disabled={purgeConfirmInput !== 'DELETE' || purgeMutation.isPending}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: purgeConfirmInput === 'DELETE' && !purgeMutation.isPending
                    ? 'var(--color-danger)'
                    : 'var(--color-surface-muted)',
                  color: purgeConfirmInput === 'DELETE' && !purgeMutation.isPending
                    ? 'var(--color-text-inverse)'
                    : 'var(--color-text-muted)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: purgeConfirmInput === 'DELETE' && !purgeMutation.isPending ? 'pointer' : 'not-allowed',
                }}
              >
                {purgeMutation.isPending ? '...' : t('auditLog.purge.step2.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 퍼지 완료 토스트 */}
      {purgeToast !== null && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 1100,
            background: 'var(--color-surface-overlay, #1e1e2e)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {purgeToast}
        </div>
      )}

      {/* 반응형 스타일 */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          table { min-width: 600px; }
        }
      `}</style>
    </div>
  );
}
